/**
 * DataLayer utility for GTM/GA4 e-commerce events
 * Implements Google Analytics 4 e-commerce dataLayer events
 */

const DEBUG = process.env.NODE_ENV !== 'production';

/**
 * AMD → USD conversion rate.
 * Starts with a static fallback (390); updated once per session from /api/exchange-rate
 * which proxies the Central Bank of Armenia and caches the result for 24 hours.
 */
let AMD_TO_USD_RATE = 390;

if (typeof window !== 'undefined') {
	fetch('/api/exchange-rate')
		.then((res) => res.json())
		.then(({ rate }) => {
			if (rate && rate > 0) AMD_TO_USD_RATE = rate;
		})
		.catch(() => {
			/* keep fallback */
		});
}

/**
 * Convert an AMD amount to USD, rounded to 2 decimal places.
 */
const toUSD = (amdAmount) => Math.round((parseFloat(amdAmount || 0) / AMD_TO_USD_RATE) * 100) / 100;

/**
 * Initialize dataLayer if it doesn't exist
 */
const initDataLayer = () => {
	if (typeof window !== 'undefined' && !window.dataLayer) {
		window.dataLayer = [];
	}
};

/**
 * Push event to dataLayer
 */
const pushToDataLayer = (eventData) => {
	// if (DEBUG) console.log('pushToDataLayer', eventData);
	if (typeof window === 'undefined') return;

	initDataLayer();
	window.dataLayer.push(eventData);
};

/**
 * For GA4 ecommerce via GTM: clear previous ecommerce object, then push the new event.
 * This prevents stale ecommerce data from leaking into subsequent events.
 */
const pushEcommerceEvent = (eventData) => {
	// Recommended GA4 pattern when using dataLayer + GTM
	pushToDataLayer({ ecommerce: null });
	pushToDataLayer(eventData);
};

/**
 * Format cart item for dataLayer
 * @param {Object} item - Cart item from store
 * @returns {Object} Formatted item for dataLayer
 */
const formatCartItem = (item) => {
	if (!item || !item.product) return null;

	const product = item.product;
	const price = toUSD(product.discountedPrice || product.price || 0);
	const quantity = parseInt(item.qty || 1, 10);

	// Get variant name if available
	let itemVariant = null;
	if (item.selectedVariant && item.selectedOption) {
		itemVariant = `${item.selectedVariant.name || ''} - ${item.selectedOption.name || ''}`.trim();
	} else if (item.selectedVariant?.name) {
		itemVariant = item.selectedVariant.name;
	} else if (item.selectedOption?.name) {
		itemVariant = item.selectedOption.name;
	}

	// Get category - try multiple possible fields
	const category = product.category?.name || product.category_name || product.categories?.[0]?.name || 'Sushi'; // fallback

	return {
		item_id: product.sku || product.id?.toString() || '',
		item_name: product.name || '',
		item_category: category,
		price: price,
		quantity: quantity,
		...(itemVariant && { item_variant: itemVariant }),
	};
};

/**
 * Format multiple cart items for dataLayer
 */
const formatCartItems = (items) => {
	if (!items || !Array.isArray(items)) return [];

	return items
		.filter((item) => item && !item.is_gift) // Exclude gifts
		.map(formatCartItem)
		.filter(Boolean); // Remove null items
};

/**
 * Calculate total value from items
 */
const calculateTotalValue = (items) => {
	if (!items || !Array.isArray(items)) return 0;

	const amdTotal = items
		.filter((item) => item && !item.is_gift)
		.reduce((total, item) => {
			const price = parseFloat(item.product?.discountedPrice || item.product?.price || 0);
			const qty = parseInt(item.qty || 1, 10);
			return total + price * qty;
		}, 0);

	return toUSD(amdTotal);
};

/**
 * VIEW_ITEM - When a product is viewed
 */
export const trackViewItem = (product, options = {}) => {
	const price = toUSD(product.discountedPrice || product.sale_price || product.price || 0);
	const category = product.category?.name || product.category_name || product.categories?.[0]?.name || 'Sushi';

	let itemVariant = null;
	if (options.variant && options.option) {
		itemVariant = `${options.variant.name || ''} - ${options.option.name || ''}`.trim();
	} else if (options.variant?.name) {
		itemVariant = options.variant.name;
	} else if (options.option?.name) {
		itemVariant = options.option.name;
	}

	const eventData = {
		event: 'view_item',
		ecommerce: {
			currency: 'USD',
			items: [
				{
					item_id: product.sku || product.id?.toString() || '',
					item_name: product.name || '',
					item_category: category,
					price: price,
					quantity: 1,
					...(itemVariant && { item_variant: itemVariant }),
				},
			],
		},
	};

	// if (DEBUG) console.log('viewItem', eventData);
	pushEcommerceEvent(eventData);
};

/**
 * ADD_TO_CART - When an item is added to cart
 */
export const trackAddToCart = (item) => {
	const formattedItem = formatCartItem(item);
	if (!formattedItem) return;

	const amdPrice = parseFloat(item.product?.discountedPrice || item.product?.price || 0);
	const quantity = parseInt(item.qty || 1, 10);
	const value = toUSD(amdPrice * quantity);

	const eventData = {
		event: 'add_to_cart',
		ecommerce: {
			currency: 'USD',
			value: value,
			items: [formattedItem],
		},
	};

	// if (DEBUG) console.log('addToCart', eventData);
	pushEcommerceEvent(eventData);
};

/**
 * BEGIN_CHECKOUT - When checkout page is loaded
 */
export const trackBeginCheckout = (items) => {
	const formattedItems = formatCartItems(items);

	if (formattedItems.length === 0) return;

	const value = calculateTotalValue(items);

	const eventData = {
		event: 'begin_checkout',
		ecommerce: {
			currency: 'USD',
			value: value,
			items: formattedItems,
		},
	};

	// if (DEBUG) console.log('beginCheckout', eventData);
	pushEcommerceEvent(eventData);
};

/**
 * ADD_SHIPPING_INFO - When shipping address is added
 */
export const trackAddShippingInfo = (items, shippingTier = 'Standard Shipping') => {
	const formattedItems = formatCartItems(items);
	if (formattedItems.length === 0) return;

	const value = calculateTotalValue(items);

	const eventData = {
		event: 'add_shipping_info',
		ecommerce: {
			currency: 'USD',
			value: value,
			shipping_tier: shippingTier,
			items: formattedItems,
		},
	};

	// if (DEBUG) console.log('addShippingInfo', eventData);
	pushEcommerceEvent(eventData);
};

/**
 * ADD_PAYMENT_INFO - When payment method is selected
 */
export const trackAddPaymentInfo = (items, paymentType = 'Credit Card') => {
	const formattedItems = formatCartItems(items);
	if (formattedItems.length === 0) return;

	const value = calculateTotalValue(items);

	const eventData = {
		event: 'add_payment_info',
		ecommerce: {
			currency: 'USD',
			value: value,
			payment_type: paymentType,
			items: formattedItems,
		},
	};

	// if (DEBUG) console.log('addPaymentInfo', eventData);
	pushEcommerceEvent(eventData);
};

/**
 * PURCHASE - When order is completed
 */
export const trackPurchase = (orderData, items, orderType = 'one-off') => {
	const formattedItems = formatCartItems(items);
	if (formattedItems.length === 0) return;

	const transactionId = orderData.transaction_id || orderData.orderNumber || orderData.order_id || '';

	if (!transactionId) {
		console.warn('Purchase event requires transaction_id');
		return;
	}

	const eventData = {
		event: 'purchase',
		ecommerce: {
			transaction_id: transactionId,
			affiliation: orderData.affiliation || 'Online Store',
			currency: 'USD',
			type: orderType,
			value: toUSD(orderData.total),
			shipping: toUSD(orderData.shipping),
			coupon: orderData.coupon,
			items: formattedItems,
		},
	};

	// if (DEBUG) console.log('purchase', eventData);
	pushEcommerceEvent(eventData);
};
